import React, { createElement, createContext } from 'react'
import { ExperienceProvider } from 'react-chansey'
import { ConditionalWrapper } from '../../utils/conditional-wrapper'

import { isObject } from '../../utils/obj'
import { isDev } from '../../server/helpers/env'

/**
 * Context APIs & Providers to for the options & component maps
 */

/* Context slimming */
// TODO: Remove or implement
export const SlimlineContext = createContext([])
/**
 * Map for the components that eevee should include
 */
export const EVCComponentContext = createContext({})
const EVCComponentProvider = EVCComponentContext.Provider

/**
 * Provider for the contentful options
 */
export const EVCOptionsContext = createContext(() => ({}))
const EVCOptionsProvider = EVCOptionsContext.Provider

/**
 * Provider for the data set that eevee uses
 */
export const EVCDataContext = createContext({})
const EVCDataProvider = EVCDataContext.Provider

/**
 * Experience provider for audience & testing toggles
 */
const EVExperienceProvider = ExperienceProvider

export const EVTrackingContext = createContext({})
const EVTrackingProvider = ({ children, value: config }) => {
  const track = async (action, { category, label, value, ...props }, isTwinEvent) => {
    const properties = {
      category,
      label,
      value,
      ...props,
    }
    if (isTwinEvent) {
      const args = [
        [action, properties],
        [`${config.twinEventPrefix} ${action}`, properties],
      ]
      if (isDev) {
        // eslint-disable-next-line no-console
        console.log('[TRACKING] trackAll:', args)

        window.trackings = window.trackings || {}
        window.trackings.trackAll = window.trackings.trackAll || []
        window.trackings.trackAll.push(args)
      }
      if (config?.client?.trackAll && !isDev) {
        return config.client.trackAll(args)
      } else if (isDev) {
        // eslint-disable-next-line no-console
        console.log('[TRACKING] trackAll not set')
      }
    } else {
      return new Promise((resolve) => {
        const args = [action, properties, resolve]
        if (isDev) {
          // eslint-disable-next-line no-console
          console.log('[TRACKING] track:', args)

          window.trackings = window.trackings || {}
          window.trackings.track = window.trackings.track || []
          window.trackings.track.push(args)
          resolve()
        }
        if (config?.client?.track && !isDev) {
          return config.client.track(...args)
        }
      })
    }
  }
  return createElement(
    EVTrackingContext.Provider,
    { value: { track, client: config.client } },
    children,
  )
}

/**
 * Core provider for all three things
 *
 * @param {Object} props - The component properies.
 * @param {Object} props.components - A map of components
 * @param {Function} props.options - Function to retrieve a list of contentful Rich Text options
 * @param {Object} props.data - Your main contentful data see //TODO: Write schema for data strucuture
 * @param {Object} [props.experiences] - A list of experiences
 * @param {any} props.children - The Main body of your application
 *
 * @returns {*} Your app
 */
const EVCoreProvider = ({ components, options, data, experiences, tracking, children }) => (
  <EVCDataProvider value={data}>
    <EVCComponentProvider value={components}>
      <EVCOptionsProvider value={options}>
        <ConditionalWrapper
          condition={Boolean(tracking)}
          wrapper={EVTrackingProvider}
          value={tracking}
        >
          <ConditionalWrapper
            condition={isObject(experiences)}
            wrapper={EVExperienceProvider}
            value={experiences}
          >
            {children}
          </ConditionalWrapper>
        </ConditionalWrapper>
      </EVCOptionsProvider>
    </EVCComponentProvider>
  </EVCDataProvider>
)

export default EVCoreProvider
